import { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import api, { apiError } from '../../services/api';
import { useApi } from '../../hooks/useApi';
import Select from '../../components/ui/Select';
import StatusPill from '../../components/StatusPill';
import { CodeChip } from '../../components/ui/CountrySelect';
import { Card, EmptyState, LoadingRows, Notice, PageHeader, SearchInput, Segmented, Toggle } from '../../components/ui/Page';
import { countryName } from '../../lib/countries';
import { ago, companyDisplayName, titleCase } from '../../lib/format';
import CompanyLogo from '../../components/CompanyLogo';

type Person = { id: string; name: string | null; email: string } | null;

type Company = {
  id: string;
  name: string;
  logicalName?: string | null;
  hasLogo?: boolean;
  email: string;
  phone: string | null;
  countryCode: string;
  sector: string | null;
  status: string;
  isActive: boolean;
  emailSentAt: string | null;
  updatedAt: string;
  customer: Person;
  assignedAdmin: Person;
  ratings: { version: number; grade: string; score: number; status: string }[];
  _count: { documents: number; remediations: number };
};

const STAGES = ['INVITED', 'INTAKE', 'SUBMITTED', 'RATED', 'REMEDIATING', 'REANALYSIS', 'READY', 'OUTREACH', 'FUNDED'];

export async function setCompanyAccess(company: { id: string; name: string; logicalName?: string | null }, isActive: boolean) {
  if (!isActive && !window.confirm(`Disable ${companyDisplayName(company)}? It disappears from the customer's workspace until you enable it again.`)) return false;
  await api.patch(`/admin/companies/${company.id}/access`, { isActive });
  return true;
}

export default function CompaniesPage() {
  const navigate = useNavigate();
  const created = (useLocation().state as { emailSent?: boolean } | null)?.emailSent;
  const [scope, setScope] = useState<'all' | 'assigned'>('all');
  const [q, setQ] = useState('');
  const [country, setCountry] = useState('');
  const [stage, setStage] = useState('');
  const [access, setAccess] = useState<'all' | 'active' | 'disabled'>('all');
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ tone: 'success' | 'error'; text: string } | null>(
    created === undefined ? null : created ? { tone: 'success', text: 'Company added and the invite email was sent.' } : { tone: 'error', text: 'Company added, but the invite email could not be sent — check INAI_API_KEY.' },
  );
  const { data, loading, error, reload } = useApi<{ companies: Company[] }>('/admin/companies', { scope });
  const { data: teamData } = useApi<{ admins: { id: string; name: string | null; email: string; isActive: boolean }[] }>('/admin/team');
  const companies = data?.companies || [];
  const admins = (teamData?.admins || []).filter((admin) => admin.isActive);

  const reassign = async (company: Company, adminId: string) => {
    setBusy(company.id);
    setNotice(null);
    try {
      await api.patch(`/admin/companies/${company.id}/assign`, { adminId });
      setNotice({ tone: 'success', text: `${companyDisplayName(company)} assignment updated.` });
      await reload();
    } catch (e) {
      setNotice({ tone: 'error', text: apiError(e, 'Could not reassign the company.') });
    } finally {
      setBusy(null);
    }
  };

  const countryOptions = useMemo(() => {
    const counts = new Map<string, number>();
    companies.forEach((c) => counts.set(c.countryCode, (counts.get(c.countryCode) || 0) + 1));
    return [
      { value: '', label: 'All countries' },
      ...Array.from(counts, ([code, n]) => ({ value: code, label: countryName(code), badge: String(n), prefix: <CodeChip code={code} /> })).sort((a, b) => a.label.localeCompare(b.label)),
    ];
  }, [companies]);

  const shown = useMemo(() => {
    const term = q.trim().toLowerCase();
    return companies.filter(
      (c) =>
        (!country || c.countryCode === country) &&
        (!stage || c.status === stage) &&
        (access === 'all' || (access === 'active' ? c.isActive : !c.isActive)) &&
        (!term || [c.logicalName, c.name, c.email, c.sector, c.customer?.email, c.customer?.name].some((v) => v?.toLowerCase().includes(term))),
    );
  }, [companies, q, country, stage, access]);

  const toggle = async (c: Company, next: boolean) => {
    setBusy(c.id);
    setNotice(null);
    try {
      if (await setCompanyAccess(c, next)) {
        setNotice({ tone: 'success', text: `${companyDisplayName(c)} ${next ? 'enabled' : 'disabled'}.` });
        await reload();
      }
    } catch (e) {
      setNotice({ tone: 'error', text: apiError(e, 'Could not update the company.') });
    } finally {
      setBusy(null);
    }
  };

  const disabledCount = companies.filter((c) => !c.isActive).length;

  return (
    <div>
      <PageHeader
        title="Companies"
        description="All companies on Micus. Disable a company to hide it from its customer's workspace."
        actions={
          <Link to="/companies/new" className="mz-btn-primary">
            + Add company
          </Link>
        }
      />

      <Card className="mz-fade-up" padded={false}>
        <div className="flex flex-wrap items-center gap-2 border-b border-[#fbeef4] p-3">
          <SearchInput value={q} onChange={setQ} placeholder="Search company, email, sector or customer…" className="min-w-[220px] flex-1" />
          <Select value={country} onChange={setCountry} options={countryOptions} className="w-48" searchable />
          <Select
            value={stage}
            onChange={setStage}
            className="w-44"
            options={[{ value: '', label: 'All stages' }, ...STAGES.map((s) => ({ value: s, label: titleCase(s), badge: String(companies.filter((c) => c.status === s).length) }))]}
          />
          <Segmented
            value={access}
            onChange={setAccess}
            options={[
              { value: 'all', label: 'All', count: companies.length },
              { value: 'active', label: 'Enabled' },
              { value: 'disabled', label: 'Disabled', count: disabledCount },
            ]}
          />
          <Segmented
            value={scope}
            onChange={setScope}
            options={[
              { value: 'all', label: 'Everyone' },
              { value: 'assigned', label: 'Mine' },
            ]}
          />
        </div>

        {(notice || error) && (
          <div className="px-3 pt-3">
            <Notice tone={notice?.tone || 'error'} onClose={() => setNotice(null)}>
              {notice?.text || error}
            </Notice>
          </div>
        )}

        {loading && !data ? (
          <LoadingRows />
        ) : shown.length === 0 ? (
          <EmptyState
            title={companies.length ? 'No companies match' : 'No companies yet'}
            description={companies.length ? 'Adjust the filters to see more.' : 'Invite the first company — Micus emails the customer a workspace link.'}
            action={!companies.length && <Link to="/companies/new" className="mz-btn-primary">+ Add company</Link>}
          />
        ) : (
          <div className="w-full overflow-x-auto">
            <table className="mz-table w-full table-fixed">
              <colgroup>
                <col className="w-[26%]" />
                <col className="w-[14%]" />
                <col className="w-[12%]" />
                <col className="w-[10%]" />
                <col className="w-[20%]" />
                <col className="w-[10%]" />
                <col className="w-[8%]" />
              </colgroup>
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Customer</th>
                  <th>Stage</th>
                  <th>Rating</th>
                  <th>Assigned admin</th>
                  <th>Updated</th>
                  <th className="text-right">Enabled</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((c) => {
                  const r = c.ratings[0];
                  return (
                    <tr key={c.id} className={`${c.customer ? 'mz-row-link' : ''} ${c.isActive ? '' : 'opacity-60'}`} onClick={() => c.customer && navigate(`/users/${c.customer.id}`)}>
                      <td>
                        <div className="flex min-w-0 items-center gap-2">
                          <CompanyLogo companyId={c.id} name={companyDisplayName(c)} hasLogo={c.hasLogo} className="h-8 w-8 shrink-0" />
                          <div className="min-w-0">
                            <p className={`truncate font-semibold ${c.isActive ? '' : 'line-through'}`}>{companyDisplayName(c)}</p>
                            <p className="truncate text-xs text-[#a77d92]">{c.sector || c.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="min-w-0 text-[13px]">
                        {c.customer ? (
                          <span className="block truncate">{c.customer.name || c.customer.email}</span>
                        ) : (
                          <span className="text-xs text-[#b45309]">{c.emailSentAt ? 'Invite sent' : 'Not claimed'}</span>
                        )}
                      </td>
                      <td>
                        <StatusPill label={titleCase(c.status)} tone={c.status === 'FUNDED' ? 'green' : c.status === 'INVITED' ? 'amber' : 'pink'} />
                      </td>
                      <td className="whitespace-nowrap text-[13px]">
                        {r ? (
                          <>
                            <span className="font-semibold">{r.grade}</span>
                            <span className="text-xs text-[#a77d92]"> · {r.score}</span>
                          </>
                        ) : (
                          <span className="text-[#c9a3b5]">—</span>
                        )}
                      </td>
                      <td className="min-w-0" onClick={(e) => e.stopPropagation()}>
                        <Select
                          value={c.assignedAdmin?.id || ''}
                          onChange={(id) => void reassign(c, id)}
                          disabled={busy === c.id || admins.length === 0}
                          className="w-full max-w-full"
                          searchable
                          options={admins.map((admin) => ({ value: admin.id, label: admin.name || admin.email, description: admin.name ? admin.email : undefined }))}
                          placeholder="Assign admin"
                        />
                      </td>
                      <td className="whitespace-nowrap text-xs text-[#9a6b82]">{ago(c.updatedAt)}</td>
                      <td className="text-right" onClick={(e) => e.stopPropagation()}>
                        <Toggle size="sm" checked={c.isActive} disabled={busy === c.id} onChange={(v) => void toggle(c, v)} label={c.isActive ? 'Disable company' : 'Enable company'} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
